import PropTypes from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";

// Defaults builders
import { ComplexCommissionTier, GrowthBonusTier, getGrossReferenceTitle } from "@/PayPlans/helpers/DefaultSections";

// Hooks
import { useCallbackState } from "@/Common/hooks/CallbackState";
import { useValidations } from "@/Common/hooks/Validations";

// Layouts
import TierAlertError from "@/Layouts/components/Tiers/TierAlertError";
import TierColumns from "@/Layouts/components/Tiers/TierColumns";
import TierColumnsItem from "@/Layouts/components/Tiers/TierColumnsItem";
import TierRows from "@/Layouts/components/Tiers/TierRows";

// Custom components
import FrequencySelector from "@/PayPlans/components/Commons/FrequencySelector";
import GrossBonusOptions from "@/PayPlans/components/Defaults/Managers/Departmental/GrossBonusOptions";
import MatrixColumn from "./MatrixColumn";
import MatrixRow from "./MatrixRow";

// MUI
import { Collapse, Grid, Stack, Typography } from "@mui/material";
import { TransitionGroup } from "react-transition-group";

const minimumColumns = 1;
const maximumColumns = 4;
const maximumTiers = Number.MAX_VALUE;

const minimumGrossInput = 0;
const maximumGrossInput = 1000000;

const minimumGrowthInput = -100;
const maximumGrowthInput = 100;

const hasOptionsDisabled = (tiers = []) => {
	const firstTier = tiers?.[0] || {};
	const { bonuses } = firstTier;
	const value = bonuses?.[0]?.bonus?.value;
	return !value;
};

const getDefaultGrossOptions = (sectionId) => {
	return {
		bodyShop: sectionId === "bodyShop",
		parts: sectionId === "parts",
		service: sectionId === "service",
	};
};

export default function GrossBonus(props) {
	const {
		sectionId = "",
		identifier = "",
		make = "",
		grossOptions = {
			bodyShop: false,
			parts: false,
			service: false,
		},
		frequency = "MoM",
		tiers = [],
		isEmptyBonus = false,
		readOnly = false,
		onActionUpdate = () => {},
		onActionValidation = () => {},
	} = props;

	const [disableAddColumn, setDisableAddColumn] = useState(false);
	// Hooks
	const { errors, isValid, getErrorsMessages, validate } = useValidations();
	const localErrorsMessages = getErrorsMessages();

	const headerErrors = errors?.get("grossGrowth");
	const rowsErrors = errors?.get("totalGross");
	const columnsErrors = errors?.get("bonuses");

	// Control to send update actions
	const wasUpdated = useRef(false);

	const onModelUpdate = (updatedModel) => {
		onActionValidation?.(identifier, isValid);
		if (wasUpdated.current) {
			wasUpdated.current = false;
			const isOptionsDisabled = hasOptionsDisabled(updatedModel?.tiers);
			if (isOptionsDisabled) {
				updatedModel["grossOptions"] = getDefaultGrossOptions(sectionId);
			}
			onActionUpdate?.(identifier, updatedModel);
		}
	};

	const [innerModel, setInnerModel] = useCallbackState(
		{
			tiers: [],
			frequency,
		},
		onModelUpdate
	);

	// Action handlers
	const handleUpdateFrequency = (selection) => {
		update({ ...innerModel, frequency: selection.value });
	};

	const handleUpdateGrossOptions = (options) => {
		update({ ...innerModel, grossOptions: options });
	};

	const handleAddColumn = () => {
		const updatedTiers = [...innerModel.tiers];
		const bonusInput = updatedTiers?.[0]?.["bonuses"]?.[0]?.["bonus"]?.["input"] || "Percent";
		const lastIndex = updatedTiers?.[0]?.["bonuses"]?.length ? updatedTiers[0]["bonuses"].length - 1 : 0;

		updatedTiers.forEach((tier) => {
			tier.bonuses.push(GrowthBonusTier(minimumGrowthInput, maximumGrowthInput, { bonus: 0, input: bonusInput }));
			if (tier?.bonuses?.[lastIndex]) {
				tier.bonuses[lastIndex]["to"]["value"] = minimumGrowthInput;
			}
		});

		wasUpdated.current = true;
		setInnerModel({ ...innerModel, tiers: updatedTiers });
	};

	const handleDeleteColumn = () => {
		const updatedTiers = [...innerModel.tiers];
		if (updatedTiers?.[0]?.["bonuses"]?.length > minimumColumns) {
			updatedTiers.forEach((tier) => {
				tier.bonuses.splice(-1);
				const lastIndex = tier?.bonuses?.length ? tier.bonuses.length - 1 : 0;
				tier.bonuses[lastIndex]["to"]["value"] = maximumGrowthInput;
			});
		}
		update({ ...innerModel, tiers: updatedTiers });
	};

	const handleAddTier = () => {
		const updatedTiers = [...innerModel.tiers];
		const lastIndex = updatedTiers?.length ? updatedTiers.length - 1 : 1;
		const lastInput = updatedTiers?.[lastIndex]?.["bonuses"]?.[0]?.["bonus"]?.["input"] || "Percent";
		const previousBonusesLength = updatedTiers?.[lastIndex]?.["bonuses"]?.length;

		const bonuses = [];
		for (let i = 0; i < previousBonusesLength; i++) {
			const previousFrom = updatedTiers?.[0]?.["bonuses"]?.[i]?.["from"]?.["value"] || 0;
			const previousTo = updatedTiers?.[0]?.["bonuses"]?.[i]?.["to"]?.["value"] || 0;
			bonuses[i] = GrowthBonusTier(previousFrom, previousTo, { bonus: 0, input: lastInput });
		}

		updatedTiers.push(
			ComplexCommissionTier([minimumGrossInput + 0.01, "Dollar"], [maximumGrossInput, "Dollar"], {
				identifier: "bonuses",
				commissions: bonuses,
			})
		);

		if (updatedTiers?.length) {
			updatedTiers[lastIndex].to.value = minimumGrossInput;
		}

		wasUpdated.current = true;
		setInnerModel({ ...innerModel, tiers: updatedTiers });
	};

	const handleDeleteTier = () => {
		const updatedTiers = [...innerModel.tiers];
		if (updatedTiers.length >= 1) {
			updatedTiers.splice(-1);
		}
		const lastIndex = updatedTiers?.length ? updatedTiers.length - 1 : 0;
		updatedTiers[lastIndex].to.value = maximumGrossInput;
		update({ ...innerModel, tiers: updatedTiers });
	};

	const handleUpdateValue = (row, column, location, area, value) => {
		const updatedTiers = [...innerModel.tiers];
		const updatedValue = value ? Number(value) : 0;

		switch (location) {
			case "column":
				updatedTiers.forEach((tier) => {
					tier.bonuses?.forEach((bonus, key) => {
						if (area in bonus && key === column) {
							bonus[area]["value"] = updatedValue;
							if (tier.bonuses?.[key + 1]) {
								tier.bonuses[key + 1]["from"]["value"] = (updatedValue + 0.01).toFixed(2);
							}
						}
					});
				});
				break;
			case "row":
				if (area !== "bonus") {
					updatedTiers[row][area]["value"] = updatedValue;
					if (updatedTiers?.[row + 1] && area === "to") {
						updatedTiers[row + 1]["from"]["value"] = (updatedValue + 0.01).toFixed(2);
					}
				} else {
					updatedTiers[row]["bonuses"][column][area]["value"] = updatedValue;
				}
				break;
			default:
				break;
		}

		update({ ...innerModel, tiers: updatedTiers });
	};

	const handleUpdateInput = (_row, _column, location, area, input) => {
		const updatedTiers = [...innerModel.tiers];
		switch (location) {
			case "column":
				break;
			case "row":
				updatedTiers.forEach((tier) => {
					tier.bonuses?.forEach((bonus) => {
						if (area in bonus) {
							bonus[area]["input"] = input;
							bonus[area]["value"] = 0;
						}
					});
				});
				break;
			default:
				break;
		}
		update({ ...innerModel, tiers: updatedTiers });
	};

	const update = (updatedModel) => {
		wasUpdated.current = true;
		setInnerModel(updatedModel);
	};

	useMemo(() => {
		const innerTiers = innerModel?.tiers || [];
		if (innerTiers?.length) {
			validate([
				{
					definition: {
						useKey: "totalGross",
						operators: {
							conditions: [
								{
									type: "INLINE",
									on: "value",
									compare: "<", // If from < to is good,
								},
								{
									type: "STEP",
									on: "value",
									compare: "===",
									deltas: { Percent: 0.01, Dollar: 0.01 }, // Add delta on upper value
									distinct: 0,
								},
							],
						},
						errorsMap: ["from", "to"],
						message: "Invalid tiers",
						validationType: "DOUBLE_INPUT",
						specialProperties: {
							noFirstError: true,
							noLastError: true,
						},
					},
					section: innerTiers || [],
				},
				{
					definition: {
						useKey: "grossGrowth",
						operators: {
							conditions: [
								{
									type: "INLINE",
									on: "value",
									compare: "<", // If from < to is good,
								},
								{
									type: "STEP",
									on: "value",
									compare: "===",
									deltas: { Percent: 0.01, Dollar: 0.01 }, // Add delta on upper value
								},
							],
						},
						errorsMap: ["from", "to"],
						message: "Invalid Gross Growth tiers",
						validationType: "DOUBLE_INPUT",
						specialProperties: {
							noFirstError: true,
							noLastError: true,
						},
					},
					section: innerTiers?.[0]?.bonuses || [],
				},
				{
					definition: {
						useKey: "bonuses",
						operators: {
							conditions: [
								{
									order: "ROWS",
									type: "STEP",
									on: "value",
									compare: "<=", // If current <= next is good,
								},
								{
									order: "COLUMNS",
									type: "STEP",
									on: "value",
									compare: "<",
									deltas: { Dollar: 0, Percent: 0 },
								},
							],
						},
						errorsMap: ["bonus"],
						errorsMapOn: "bonuses",
						message: "Incorrect bonuses definition",
						validationType: "MATRIX",
					},
					section: innerTiers,
				},
			]);
		}
	}, [innerModel, validate]);

	const handleDisableFrequency = (disabledFrequencies) => {
		setDisableAddColumn(disabledFrequencies?.length === 2);
	};

	useEffect(() => {
		setInnerModel({
			tiers: clone(tiers),
			frequency,
		});

		return () => {
			setInnerModel({ tiers: [], frequency });
			wasUpdated.current = false;
		};
	}, [tiers, frequency]);

	return (
		<Grid container item xs={12}>
			<Grid item xs={12} mb={1}>
				<Stack direction={"row"} alignItems={"center"} spacing={5}>
					<Stack className="min-w-[210px]">
						<Typography className={"tiered-title regular-montserrat-label fs-18"}>
							{getGrossReferenceTitle(sectionId)}
						</Typography>
					</Stack>
					<Stack>
						<GrossBonusOptions
							defaultIdentifier={sectionId}
							options={grossOptions}
							readOnly={readOnly || hasOptionsDisabled(innerModel?.tiers)}
							onChangeOptions={(options) => {
								handleUpdateGrossOptions(options);
							}}
						/>
					</Stack>
				</Stack>
			</Grid>
			<Grid item xs={12} mt={1.4}>
				<TierColumns
					titleLabel={"Gross Growth"}
					titleContainerProps={{
						justifyContent: "flex-start",
						alignItems: "center",
						sx: { maxWidth: "241px", width: "241px", marginRight: "33px", marginLeft: "-29px" },
					}}
					titleProps={{
						className: "tiered-title",
						sx: { mt: "3px" },
					}}
					disabledDelete={innerModel?.tiers?.[0]?.bonuses?.length <= minimumColumns || readOnly}
					disabledAdd={disableAddColumn || innerModel?.tiers?.[0]?.bonuses?.length >= maximumColumns || readOnly}
					onClickDelete={() => handleDeleteColumn()}
					onClickAdd={() => handleAddColumn()}
				>
					<Stack direction={"row"} position={"relative"}>
						{innerModel?.tiers?.[0]?.bonuses?.map((tier, key) => {
							const fromDisabled = Boolean(!key);
							const toDisabled = Boolean(innerModel?.tiers?.[0]?.bonuses.length === key + 1);

							return (
								<TierColumnsItem key={`column_${sectionId}_${identifier}_${key}`} index={key} title={"Comm."}>
									<MatrixColumn
										index={key}
										item={tier}
										errors={headerErrors || []}
										fromDisabled={fromDisabled}
										toDisabled={toDisabled}
										readOnly={readOnly}
										onActionUpdate={handleUpdateValue}
									/>
								</TierColumnsItem>
							);
						})}
					</Stack>
				</TierColumns>

				<Stack mt={"-40px"}>
					<FrequencySelector
						make={make}
						section={"DefaultGross"}
						sx={{ width: "200px" }}
						disabled={readOnly}
						readOnly={readOnly}
						onChange={(selection) => handleUpdateFrequency(selection)}
						sectionId={sectionId}
						onDisabledFrequency={handleDisableFrequency}
						selected={innerModel?.frequency}
					/>
				</Stack>
			</Grid>

			<Grid item xs={12} mt={2} minHeight={"105px"}>
				<TierRows
					showTitle
					title={"Total Gross"}
					sxTitlePropsContainer={{
						top: 110,
						left: 0,
					}}
					centerTitleAfterRow={2}
					deleteButtonText={"Remove Tier"}
					addButtonText={"Add Tier"}
					deleteButtonDisabled={innerModel?.tiers?.length <= 1 || readOnly}
					addButtonDisabled={innerModel?.tiers?.length >= maximumTiers || readOnly}
					spaces={{
						mainSection: 235,
						columns: 195,
						totalColumns: innerModel?.tiers?.[0]?.bonuses?.length || 1,
						totalRows: innerModel?.tiers?.length || 1,
						separation: 5.5,
					}}
					rowsAreaProps={{
						ml: "35px",
					}}
					onClickDelete={() => handleDeleteTier()}
					onClickAdd={() => handleAddTier()}
				>
					<TransitionGroup>
						<Collapse>
							<Stack direction="column" rowGap={2}>
								{innerModel?.tiers?.map((tier, key) => {
									const fromDisabled = Boolean(!key);
									const toDisabled = Boolean(innerModel?.tiers?.length === key + 1);
									const bonusesLength = innerModel?.tiers?.[0]?.bonuses?.length || 0;
									const reduction = bonusesLength * 10 + 47 + bonusesLength;

									return (
										<Grid key={`row_${sectionId}_${identifier}_${key}`} container item>
											<MatrixRow
												index={key}
												item={tier}
												fromDisabled={fromDisabled}
												toDisabled={toDisabled}
												spaces={{
													reduction,
													mainSection: 235,
													columns: 195,
													totalColumns: innerModel?.tiers?.[0]?.bonuses?.length || 1,
													totalRows: innerModel?.tiers?.length || 1,
													separation: 5.5,
												}}
												isEmptyBonus={isEmptyBonus}
												rowsErrors={rowsErrors || []}
												columnsErrors={columnsErrors || []}
												readOnly={readOnly}
												spacing={12.5}
												sxMainContent={{ minWidth: "190.5px", marginRight: "-12px !important" }}
												sxCells={{ minWidth: "95px" }}
												onActionUpdate={handleUpdateValue}
												onChangeType={handleUpdateInput}
											/>
										</Grid>
									);
								})}
							</Stack>
						</Collapse>
					</TransitionGroup>

					{Boolean(localErrorsMessages?.length || isEmptyBonus) && (
						<Grid container direction="column" rowSpacing={0.2} mt={1} alignItems="flex-start">
							{localErrorsMessages.map((message, key) => (
								<TierAlertError key={`error_gross_bonus_${sectionId}_${identifier}_${key}`} text={message} />
							))}

							{isEmptyBonus && (
								<TierAlertError
									key={`error_gross_bonus_${sectionId}_${identifier}`}
									text={"You must define at least one bonus."}
								/>
							)}
						</Grid>
					)}
				</TierRows>
			</Grid>
		</Grid>
	);
}
GrossBonus.propTypes = {
	sectionId: PropTypes.string,
	identifier: PropTypes.string,
	make: PropTypes.string,
	grossOptions: PropTypes.shape({
		bodyShop: PropTypes.bool,
		parts: PropTypes.bool,
		service: PropTypes.bool,
	}),
	frequency: PropTypes.string,
	tiers: PropTypes.array,
	isEmptyBonus: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	onActionValidation: PropTypes.func,
};
